'use client';

import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  AlertCircleIcon,
  Tick01Icon,
  Loading03Icon,
  SparklesIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '../../components/button';
import { Field } from '../../components/field';
import { Badge } from '../../components/badge';
import { Progress } from '../../components/progress';
import { Text } from '../../components/text';

export interface RoleDescriptionEditorProps {
  role?: ApplicationRoleDto;
  description: string;
  onDescriptionChange: (description: string) => void;
  hasGitIntegration?: boolean;
  onRoleAnalyze?: (roleId: string) => Promise<{
    jobId: string;
  }>;
  disabled?: boolean;
}
export function RoleDescriptionEditor({
  role,
  description,
  onDescriptionChange,
  hasGitIntegration = false,
  onRoleAnalyze,
  disabled = false,
}: RoleDescriptionEditorProps) {
  const t = useTranslations('designSystem.applicationRoles');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);
  const analysisStatus = role?.analysisStatus;
  const aiDescription = role?.aiGeneratedDescription;
  const analysisError = role?.analysisError;

  // Poll for status updates when analyzing
  useEffect(() => {
    if (analysisStatus === 'ANALYZING' || analysisStatus === 'PENDING') {
      setIsAnalyzing(true);
      const interval = setInterval(() => {
        setAnalysisProgress((prev) => Math.min(prev + 10, 90));
      }, 3000);

      return () => clearInterval(interval);
    } else {
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      if (analysisStatus === 'COMPLETED' && aiDescription) {
        setShowAiSuggestion(true);
      }
    }
  }, [analysisStatus, aiDescription]);
  const handleAnalyze = async () => {
    if (!onRoleAnalyze || !role) return;

    try {
      setIsAnalyzing(true);
      setAnalysisProgress(0);
      await onRoleAnalyze(role.id);
    } catch {
      setIsAnalyzing(false);
      setAnalysisProgress(0);
    }
  };

  const handleUseAiDescription = () => {
    if (aiDescription) {
      onDescriptionChange(aiDescription);
      setShowAiSuggestion(false);
    }
  };

  return (
    <VStack alignItems="stretch" gap={3}>
      <Field.Root>
        <Field.Label>{t('roleForm.description')}</Field.Label>
        <Field.Textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={
            disabled
              ? t('roleForm.descriptionPlaceholderAiScanning')
              : t('roleForm.descriptionPlaceholder')
          }
          rows={6}
          disabled={disabled}
        />
        <Field.HelperText>
          {disabled
            ? t('roleForm.descriptionHelperAiScanning')
            : t('roleForm.descriptionHelper')}
        </Field.HelperText>
      </Field.Root>

      {!hasGitIntegration ? (
        <Box
          p={3}
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.default"
          bg="bg.subtle"
        >
          <HStack gap={2} alignItems="center">
            <AlertCircleIcon size={16} />
            <VStack alignItems="flex-start" gap={0} flex={1}>
              <Text fontSize="sm" fontWeight="medium">
                {t('roleAnalysis.noGitIntegration')}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {t('roleAnalysis.noGitIntegrationDescription')}
              </Text>
            </VStack>
          </HStack>
        </Box>
      ) : (
        <VStack alignItems="stretch" gap={3}>
          {isAnalyzing ||
          analysisStatus === 'ANALYZING' ||
          analysisStatus === 'PENDING' ? (
            <Box
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.default"
              bg="bg.subtle"
            >
              <VStack alignItems="stretch" gap={2}>
                <HStack gap={2} alignItems="center">
                  <Box animation="spin 1s linear infinite" color="orange.9">
                    <Loading03Icon size={16} />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium">
                    {t('roleAnalysis.analyzing')}
                  </Text>
                </HStack>
                <Progress
                  value={analysisProgress}
                  max={100}
                  size="sm"
                  colorPalette="orange"
                />
                <Text fontSize="xs" color="fg.muted">
                  {t('roleAnalysis.analyzingProgress')}
                </Text>
              </VStack>
            </Box>
          ) : analysisStatus === 'COMPLETED' && aiDescription ? (
            <Box
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.default"
              bg="bg.subtle"
            >
              <VStack alignItems="stretch" gap={3}>
                <HStack
                  gap={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <HStack gap={2} alignItems="center">
                    <Tick01Icon size={16} color="var(--colors-fg-success)" />
                    <Text fontSize="sm" fontWeight="medium" color="fg.success">
                      {t('roleAnalysis.completed')}
                    </Text>
                    <Badge size="sm" variant="emphasis">
                      {t('roleAnalysis.generatedFromCode')}
                    </Badge>
                  </HStack>
                  {onRoleAnalyze && role && (
                    <Button variant="ghost" size="sm" onClick={handleAnalyze}>
                      <SparklesIcon size={14} />
                      {t('roleAnalysis.reAnalyze')}
                    </Button>
                  )}
                </HStack>
                <VStack alignItems="flex-start" gap={2}>
                  <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                    {t('roleAnalysis.aiGeneratedDescription')}:
                  </Text>
                  <Box
                    p={2}
                    borderRadius="sm"
                    bg="bg.default"
                    borderWidth="1px"
                    borderColor="border.default"
                    w="full"
                  >
                    <Text fontSize="sm" color="fg.muted" whiteSpace="pre-wrap">
                      {aiDescription}
                    </Text>
                  </Box>
                  <HStack gap={2}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUseAiDescription}
                    >
                      {t('roleAnalysis.useDescription')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAiSuggestion(!showAiSuggestion)}
                    >
                      {showAiSuggestion
                        ? t('roleAnalysis.hideSuggestion')
                        : t('roleAnalysis.viewSuggestion')}
                    </Button>
                  </HStack>
                </VStack>
              </VStack>
            </Box>
          ) : analysisStatus === 'FAILED' ? (
            <Box
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.error"
              bg="bg.subtle"
            >
              <VStack alignItems="stretch" gap={2}>
                <HStack gap={2} alignItems="center">
                  <AlertCircleIcon size={16} color="var(--colors-fg-error)" />
                  <Text fontSize="sm" fontWeight="medium" color="fg.error">
                    {t('roleAnalysis.failed')}
                  </Text>
                </HStack>
                {analysisError && (
                  <Text fontSize="sm" color="fg.error">
                    {analysisError}
                  </Text>
                )}
                {onRoleAnalyze && role && (
                  <Button variant="outline" size="sm" onClick={handleAnalyze}>
                    {t('roleAnalysis.retry')}
                  </Button>
                )}
              </VStack>
            </Box>
          ) : null}
        </VStack>
      )}
    </VStack>
  );
}
